import {Box, Button, Card, CardContent, CardHeader, Divider, FormHelperText, Grid, MenuItem} from "@mui/material";
import {BreadcrumbItem, BreadcrumbLink, Breadcrumbs, Checkbox, ErrorAlert, TextField} from "@variocube/app-ui";
import React, {useEffect, useState} from "react";
import {useAsyncCallback} from "react-async-hook";
import {Link, useNavigate} from "react-router-dom";
import {useTenantApi} from "../api";
import {Flavor, getFlavor} from "@smalox/shared";
import {useLocalization, useWordingFlavor} from "@smalox/shared";
import {slugify} from "../utils/slugify";
import {View, ViewHeader} from "./View";

export function TenantCreate() {
	const {t, language} = useLocalization();
	const wordingFlavor = useWordingFlavor();
	const {createTenant} = useTenantApi();
	const navigate = useNavigate();

	const [name, setName] = useState("");
	const [id, setId] = useState("");
	const [locale, setLocale] = useState(language);
	const [flavor, setFlavor] = useState<Flavor>(getFlavor());

	const [userGroupName, setUserGroupName] = useState(t("tenants.create.userGroupName.default"));
	const [adminGroupName, setAdminGroupName] = useState(t("tenants.create.adminGroupName.default"));
	const [userGroupSelfManaging, setUserGroupSelfManaging] = useState(true);
	const [locationName, setLocationName] = useState("");

	const [generateId, setGenerateId] = useState(true);
	const [generateLocationName, setGenerateLocationName] = useState(true);

	const submit = useAsyncCallback(async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		await createTenant({
			id,
			name,
			locale,
			flavor,
			userGroupName,
			userGroupSelfManaging,
			adminGroupName,
			locationName,
		});
		navigate(`/${id}/settings`);
	});

	useEffect(() => {
		if (generateId) {
			setId(slugify(name));
		}
	}, [name, id, generateId]);

	useEffect(() => {
		if (generateLocationName) {
			setLocationName(name);
		}
	}, [name, generateLocationName]);

	function handleChangeId(id: string) {
		setGenerateId(!id || id == slugify(name));
		setId(id);
	}

	function handleLocationNameChange(locationName: string) {
		setGenerateLocationName(!locationName || locationName == name);
		setLocationName(locationName);
	}

	return (
		<Box py={4}>
			<View maxWidth="md">
				<Breadcrumbs>
					<BreadcrumbLink component={Link} to={".."} relative="path">{t("tenants.title")}</BreadcrumbLink>
					<BreadcrumbItem>{t("tenants.create.title")}</BreadcrumbItem>
				</Breadcrumbs>
				<ViewHeader title={t("tenants.create.title")} />
				<Card>
					<form onSubmit={submit.execute}>
						<CardHeader title={t("tenants.single")} />
						<CardContent>
							<Grid container spacing={2}>
								<Grid item xs={12} md={6}>
									<TextField
										label={t("tenants.name.title")}
										helperText={t("tenants.name.helperText")}
										fullWidth
										value={name}
										onChange={setName}
										required
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<TextField
										label={t("tenants.id.title")}
										helperText={t("tenants.id.helperText")}
										fullWidth
										inputProps={{
											pattern: "[a-z0-9-]+",
										}}
										value={id}
										onChange={handleChangeId}
										required
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<TextField
										select
										label={t("tenants.locale.title") || "Language"}
										helperText={t("tenants.locale.helperText")}
										fullWidth
										value={locale}
										onChange={setLocale}
										required
									>
										<MenuItem value="de">Deutsch</MenuItem>
										<MenuItem value="en">English</MenuItem>
										<MenuItem value="fr">Français</MenuItem>
									</TextField>
								</Grid>
								<Grid item xs={12} md={6}>
									<TextField
										select
										label={t("tenants.flavor.title")}
										helperText={t("tenants.flavor.helperText")}
										fullWidth
										value={flavor}
										onChange={(value) => setFlavor(value as Flavor)}
										required
									>
										<MenuItem value="SMALOX">{t("tenants.flavor.SMALOX")}</MenuItem>
										<MenuItem value="BOXLOX">{t("tenants.flavor.BOXLOX")}</MenuItem>
										<MenuItem value="DAYLOX">{t("tenants.flavor.DAYLOX")}</MenuItem>
									</TextField>
								</Grid>
							</Grid>
						</CardContent>
						<Divider />
						<CardHeader title={t("groups.title")} />
						<CardContent>
							<Grid container spacing={2}>
								<Grid item xs={12} md={6}>
									<TextField
										label={t("tenants.create.userGroupName.title")}
										helperText={t("tenants.create.userGroupName.helperText")}
										fullWidth
										value={userGroupName}
										onChange={setUserGroupName}
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<Checkbox
										label={t(`tenants.create.userGroupSelfManaging.title.${wordingFlavor}`)}
										checked={userGroupSelfManaging}
										onChange={setUserGroupSelfManaging}
									/>
									<FormHelperText>
										{t(`tenants.create.userGroupSelfManaging.helperText.${wordingFlavor}`)}
									</FormHelperText>
								</Grid>
							</Grid>
						</CardContent>
						<CardContent>
							<Grid container spacing={2}>
								<Grid item xs={12} md={6}>
									<TextField
										label={t("tenants.create.adminGroupName.title")}
										helperText={t("tenants.create.adminGroupName.helperText")}
										fullWidth
										value={adminGroupName}
										onChange={setAdminGroupName}
									/>
								</Grid>
							</Grid>
						</CardContent>
						<Divider />
						<CardHeader title={t("locations.single")} />
						<CardContent>
							<TextField
								label={t("tenants.create.locationName.title")}
								helperText={t("tenants.create.locationName.helperText")}
								fullWidth
								value={locationName}
								onChange={handleLocationNameChange}
							/>
						</CardContent>

						{submit.error && (
							<CardContent>
								<ErrorAlert error={submit.error} />
							</CardContent>
						)}
						<CardContent>
							<Button variant="outlined" color="primary" type="submit" disabled={submit.loading}>
								{t("tenants.create.title")}
							</Button>
						</CardContent>
					</form>
				</Card>
			</View>
		</Box>
	);
}
